import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    Accordions,
    AirportOptions,
    BooleanToggleEvent,
    MapLayer,
    MapState,
    MapTheme,
    NavlevelToggleEvent,
} from 'ff/debrief/models';
import { MAP_BOUNDS } from 'ff/debrief/utils';
import DebriefMapSettingPersistor from 'ff/debrief/features/DebriefMapSettingPersistor';
import { actions as appActions } from './AppSlice';

const defaultState: MapState = {
    mapMode: '2D',
    layer: undefined,
    aeroLayer: true,
    zoom: 1,
    boundsPadding: MAP_BOUNDS,
    settingsUI: {
        accordionsExpanded: {
            cultural: false,
            airports: false,
            airspace: false,
            airways: false,
        },
    },
    settings: {
        airspace: true,
        airports: true,
        theme: 'dark',
        placeLabels: true,
        culturalElements: {
            major_roads: true,
            minor_roads: true,
            urban_areas: true,
            forests_parks: true,
            railroads: true,
            points_of_interest: true,
            peaks_passes: false,
        },
        filters: {
            airports: {
                heliports: true,
                seaplane: true,
                other: true,
                private: true,
            },
            airways: false,
            navaids: false,
            navlevel: 'low',
            waypoints: false,
        },
        airspaceFilters: {
            controlled: true,
            classE: true,
            modeC: true,
            trsa: true,
            pja: true,
            adiz: true,
            suaMoa: true,
            unknown: false,
        },
    },
};

const storedState = DebriefMapSettingPersistor.getStoredSettings();

export const mapSlice = createSlice({
    name: 'MapSlice',
    initialState: storedState || defaultState,
    reducers: {
        toggleAeroLayer: state => {
            state.aeroLayer = !state.aeroLayer;
        },
        setMapMode: (state, action: PayloadAction<MapState['mapMode']>) => {
            state.mapMode = action.payload;
        },
        setMapLayer: (state, action: PayloadAction<MapLayer>) => {
            // If currently selected, act as a toggle
            if (state.layer === action.payload) {
                state.layer = undefined;
            } else {
                state.layer = action.payload;
            }
        },
        resetMapBounds: state => {
            state.boundsPadding = MAP_BOUNDS;
        },
        setTheme: (state, action: PayloadAction<MapTheme>) => {
            state.settings.theme = action.payload;
        },
        toggleCulturalSetting: (state, action: PayloadAction<string>) => {
            //@ts-expect-error Not actual sure what is wrong here
            state.settings.culturalElements[action.payload] =
                // @ts-expect-error Not actual sure what is wrong here
                !state.settings.culturalElements[action.payload];
        },
        toggleAirspaceSetting: (state, action: PayloadAction<string>) => {
            state.settings.airspaceFilters[action.payload] =
                !state.settings.airspaceFilters[action.payload];
        },
        toggleAccordion: (state, action: PayloadAction<Accordions>) => {
            state.settingsUI.accordionsExpanded[action.payload] =
                !state.settingsUI.accordionsExpanded[action.payload];
        },
        toggleShowAirports: state => {
            state.settings.airports = !state.settings.airports;
        },
        toggleAirportType: (state, action: PayloadAction<AirportOptions>) => {
            state.settings.filters.airports[action.payload] =
                !state.settings.filters.airports[action.payload];
        },
        toggleMapSetting: (
            state,
            action: PayloadAction<BooleanToggleEvent | NavlevelToggleEvent>
        ) => {
            switch (action.payload.settingName) {
                case 'airspace':
                    state.settings.airspace = Boolean(action.payload.value);
                    break;
                case 'airways': {
                    if (typeof action.payload.value === 'boolean') {
                        state.settings.filters.airways = action.payload.value;
                    } else {
                        state.settings.filters.navlevel = action.payload.value;
                    }
                    break;
                }
                case 'placeLabels':
                    state.settings.placeLabels = action.payload
                        .value as boolean;
                    break;
                default:
                    //@ts-expect-error Because the filter value type could be any of the remaining options, ts not happy
                    state.settings.filters[action.payload.settingName] =
                        action.payload.value;
                    break;
            }
        },
    },
    extraReducers: builder => {
        builder.addCase(appActions.setChartOpen, (state, action) => {
            state.boundsPadding.bottom = action.payload
                ? MAP_BOUNDS.bottom + 300
                : MAP_BOUNDS.bottom;
        });
    },
});

export const {
    toggleAeroLayer,
    setMapMode,
    setMapLayer,
    resetMapBounds,
    toggleMapSetting,
    setTheme,
    toggleCulturalSetting,
    toggleAirspaceSetting,
    toggleAccordion,
    toggleShowAirports,
    toggleAirportType,
} = mapSlice.actions;
